import { ApolloClient } from '@apollo/client';

import {
  GeneratedGetDrawByProductIdQuery,
  GeneratedGetDrawByProductIdQueryVariables,
  GeneratedGetDrawsByMultiSetQuery,
  GeneratedGetDrawsByMultiSetQueryVariables,
  GeneratedGetDrawStatusByProductIDsQuery,
  GeneratedGetDrawStatusByProductIDsQueryVariables,
  GetDrawByProductIdDocument,
  GetDrawsByMultiSetDocument,
  GetDrawStatusByProductIDsDocument,
} from '../../../types/generated';
import { captureError } from '../../../utils/capture';
import { shopifyGraphqlIdToAdminId } from '../../../utils/shopifyIds';
import { DrawGateway } from '../types';

/**
 * Note: Currently, the rate calculations are not set correctly in shopify
 *  for the staging environment which leads to inconsistent subtotal & shipping values returned by this service
 *  Eg.: Using a Swedish shipping address would result in a subtotal and shipping cost that includes tax
 *    but if you pick the Netherlands they won't include tax
 */
export class AvantArteDrawGateway implements DrawGateway {
  // eslint-disable-next-line no-useless-constructor, no-empty-function
  constructor(private client: ApolloClient<unknown>) {}

  public async getDrawByProductId(productId: string) {
    try {
      const { data } = await this.client.query<
        GeneratedGetDrawByProductIdQuery,
        GeneratedGetDrawByProductIdQueryVariables
      >({
        query: GetDrawByProductIdDocument,
        variables: {
          productId: shopifyGraphqlIdToAdminId(productId),
        },
        fetchPolicy: 'no-cache',
      });
      return data.getDrawByProductId;
    } catch (err) {
      if (err instanceof Error) {
        captureError(err);
      }

      return void 0;
    }
  }

  public async getDrawStatusesByProductIds(productIds: string[]) {
    try {
      const { data } = await this.client.query<
        GeneratedGetDrawStatusByProductIDsQuery,
        GeneratedGetDrawStatusByProductIDsQueryVariables
      >({
        query: GetDrawStatusByProductIDsDocument,
        variables: {
          productIDs: productIds.map(shopifyGraphqlIdToAdminId),
        },
        fetchPolicy: 'no-cache',
      });
      return data.getDrawByProductIDs;
    } catch (err) {
      if (err instanceof Error) {
        captureError(err);
      }

      return void 0;
    }
  }

  public async getDrawsByMultiSet(multisetId: string) {
    try {
      const { data } = await this.client.query<
        GeneratedGetDrawsByMultiSetQuery,
        GeneratedGetDrawsByMultiSetQueryVariables
      >({
        query: GetDrawsByMultiSetDocument,
        variables: {
          multiSet: multisetId,
        },
        fetchPolicy: 'no-cache',
      });
      return data.draws.draws;
    } catch (err) {
      if (err instanceof Error) {
        captureError(err);
      }

      return void 0;
    }
  }
}
